import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import AuthenticationPrompt from '../../components/ui/AuthenticationPrompt';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DishDetailsCard from './components/DishDetailsCard';
import CustomizationSection from './components/CustomizationSection';
import DeliveryScheduling from './components/DeliveryScheduling';
import OrderSummary from './components/OrderSummary';

// Configurable tax rate
const TAX_RATE = 0.135; // 13.5%
// Configurable service fee
const SERVICE_FEE = 0; // $0

const OrderPlacement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  
  // Check if coming from shopping cart with cart items
  const cartItemsFromState = location?.state?.cartItems;
  const isCartCheckout = cartItemsFromState && cartItemsFromState?.length > 0;
  
  // Extract dish data from navigation state (for single item orders)
  const dishFromState = location?.state?.dish || location?.state?.selectedItem || location?.state?.reorder?.items?.[0];
  // Extract supplier data from navigation state
  const supplierFromState = location?.state?.supplier;
  
  // Fallback to mock data if no dish is passed and not cart checkout
  const mockDish = {
    id: 1,
    name: 'Butter Chicken with Naan',
    description: 'Tender chicken pieces cooked in a rich, creamy tomato-based sauce with aromatic spices, served with freshly baked garlic naan bread',
    image: "https://images.unsplash.com/photo-1613385109438-668d376fa611",
    imageAlt: 'Creamy orange butter chicken curry in white bowl with pieces of tender chicken in rich tomato sauce garnished with cream swirl and cilantro served alongside golden brown garlic naan bread on rustic wooden table',
    price: 14.99,
    rating: 4.8,
    reviews: 342,
    prepTime: '30-40 mins',
    tags: [
    { label: 'Indian', icon: 'Globe' },
    { label: 'Spicy', icon: 'Flame' },
    { label: 'Popular', icon: 'TrendingUp' }]

  };
  
  // Use the dish from navigation state, or fallback to mockDish
  const currentDish = dishFromState || mockDish;
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const [customizations, setCustomizations] = useState({
    spiceLevels: [
    { value: 'mild', label: 'Mild', emoji: '🌶️' },
    { value: 'medium', label: 'Medium', emoji: '🌶️🌶️' },
    { value: 'hot', label: 'Hot', emoji: '🌶️🌶️🌶️' },
    { value: 'extra-hot', label: 'Extra Hot', emoji: '🔥' }],

    selectedSpiceLevel: 'medium',
    portionSizes: [
    { value: 'small', label: 'Small', description: '1-2 people' },
    { value: 'medium', label: 'Medium', description: '2-3 people' },
    { value: 'large', label: 'Large', description: '4-5 people' }],

    selectedPortionSize: 'medium',
    dietaryOptions: [
    { value: 'vegetarian', label: 'Vegetarian', icon: 'Leaf' },
    { value: 'vegan', label: 'Vegan', icon: 'Sprout' },
    { value: 'gluten-free', label: 'Gluten-Free', icon: 'Wheat' },
    { value: 'halal', label: 'Halal', icon: 'Moon' }],

    selectedDietary: []
  });

  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user?.id) return;
      try {
        const { data, error } = await customerService.getAddresses(user.id);
        if (!error && Array.isArray(data)) {
          const normalized = data.map(addr => ({
            id: addr.id,
            label: addr.label,
            street: addr.locality || addr.street || '',
            city: addr.city,
            state: addr.state,
            zipCode: addr.pin_code || addr.zipCode || '',
            phone: addr.phone || '',
            is_primary: addr.is_primary
          }));
          setAddresses(normalized);
          // Auto-select primary address if available
          const primary = normalized.find(a => a.is_primary);
          if (primary) setSelectedAddress(primary);
        }
      } catch (err) {
        // fallback: do not set addresses
      }
    };
    fetchAddresses();
  }, [user?.id]);

  const handleCustomizationChange = (type, value) => {
    setCustomizations((prev) => ({
      ...prev,
      [`selected${type?.charAt(0)?.toUpperCase() + type?.slice(1)}`]: value
    }));
  };

  const handleScheduleChange = (type, value) => {
    if (type === 'date') {
      setScheduledDate(value);
    } else {
      setScheduledTime(value);
    }
  };

  const handleAddressChange = (address) => {
    if (address?.id) {
      setSelectedAddress(address);
    } else {
      const newAddress = { ...address, id: Date.now() };
      setAddresses((prev) => [...prev, newAddress]);
      setSelectedAddress(newAddress);
    }
  };

  const handlePlaceOrder = async () => {
    if (deliveryOption === 'delivery' && !selectedAddress) {
      alert('Please select a delivery address');
      return;
    }
    if (!scheduledDate || !scheduledTime) {
      alert('Please select delivery date and time');
      return;
    }
    
    // Place order directly with Cash on Delivery
    setIsProcessing(true);
    
    try {
      // Get customer profile
      const { data: customer, error: customerError } = await customerService?.getCustomerByUserId(user?.id);
      if (customerError) throw customerError;
      if (!customer) throw new Error('Customer profile not found');

      // Parse time slot to extract start time and convert to 24-hour format
      const parseTimeSlot = (timeSlot) => {
        if (!timeSlot) return null;
        // Extract start time from slot like "11:00 AM - 12:00 PM"
        const startTime = timeSlot?.split('-')?.[0]?.trim();
        if (!startTime) return null;
        
        // Convert 12-hour format to 24-hour format
        const [time, period] = startTime?.split(' ');
        const [hours, minutes] = time?.split(':');
        let hour24 = parseInt(hours);
        
        if (period === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
          hour24 = 0;
        }
        
        return `${hour24?.toString()?.padStart(2, '0')}:${minutes}:00`;
      };

      // Prepare estimated delivery timestamp
      const time24Format = parseTimeSlot(scheduledTime);
      
      if (!scheduledDate || !time24Format) {
        throw new Error('Invalid delivery date or time selected');
      }
      
      const estimatedDelivery = new Date(`${scheduledDate}T${time24Format}`);
      
      // Validate the date is valid
      if (isNaN(estimatedDelivery?.getTime())) {
        throw new Error('Invalid delivery date or time selected');
      }

      if (isCartCheckout) {
        // Cart checkout - create orders grouped by supplier
        const supplierGroups = cartItemsFromState?.reduce((acc, item) => {
          const supplierId = item?.menu_item?.supplier?.id;
          if (!acc?.[supplierId]) {
            acc[supplierId] = {
              supplier: item?.menu_item?.supplier,
              items: []
            };
          }
          acc?.[supplierId]?.items?.push(item);
          return acc;
        }, {});

        // Create separate order for each supplier
        for (const [supplierId, group] of Object.entries(supplierGroups)) {
          const items = group?.items?.map(item => ({
            menuItemId: item?.menu_item?.id,
            quantity: item?.quantity,
            price: Number(item?.menu_item?.price_per_plate || item?.menu_item?.price || 0)
          }));

          const groupSubtotal = items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
          const groupDeliveryFee = deliveryOption === 'delivery' ? 3.99 : 0;
          const groupServiceFee = SERVICE_FEE;
          const groupTax = (groupSubtotal + groupDeliveryFee + groupServiceFee) * TAX_RATE;
          const groupTotal = groupSubtotal + groupDeliveryFee + groupServiceFee + groupTax;

          const orderData = {
            customerId: customer?.id,
            supplierId: supplierId,
            deliveryType: deliveryOption,
            totalAmount: Number(groupTotal?.toFixed(2)),
            specialInstructions: specialInstructions,
            deliveryAddress: deliveryOption === 'delivery' 
              ? `${selectedAddress?.street}, ${selectedAddress?.city}, ${selectedAddress?.state} ${selectedAddress?.zipCode}` 
              : null,
            estimatedDelivery: estimatedDelivery?.toISOString(),
            items: items
          };

          const { error: orderError } = await customerService?.createOrder(orderData);
          if (orderError) throw orderError;
        }

        // Clear cart after successful order placement
        await customerService?.clearCart(customer?.id);
      } else {
        // Single item order (existing flow)
        const dishPrice = currentDish?.price_per_plate || currentDish?.price || 0;
        const calculatedSubtotal = Number(dishPrice) * Number(quantity);
        const calculatedDeliveryFee = deliveryOption === 'delivery' ? 3.99 : 0;
        const calculatedServiceFee = SERVICE_FEE;
        const calculatedTax = (calculatedSubtotal + calculatedDeliveryFee + calculatedServiceFee) * TAX_RATE;
        const calculatedTotal = calculatedSubtotal + calculatedDeliveryFee + calculatedServiceFee + calculatedTax;

        if (isNaN(calculatedTotal) || calculatedTotal <= 0) {
          throw new Error('Invalid order total calculated');
        }

        const orderData = {
          customerId: customer?.id,
          supplierId: currentDish?.supplier_id || currentDish?.supplierId || supplierFromState?.id,
          deliveryType: deliveryOption,
          totalAmount: Number(calculatedTotal?.toFixed(2)),
          specialInstructions: specialInstructions,
          deliveryAddress: deliveryOption === 'delivery' 
            ? `${selectedAddress?.street}, ${selectedAddress?.city}, ${selectedAddress?.state} ${selectedAddress?.zipCode}` 
            : null,
          estimatedDelivery: estimatedDelivery?.toISOString(),
          items: [
            {
              menuItemId: currentDish?.id,
              quantity: quantity,
              price: Number(dishPrice)
            }
          ]
        };

        const { error: orderError } = await customerService?.createOrder(orderData);
        if (orderError) throw orderError;
      }

      setIsProcessing(false);
      setOrderPlaced(true);
    } catch (error) {
      setIsProcessing(false);
      alert(`Failed to place order: ${error?.message}`);
      console.error('Order placement error:', error);
    }
  };

  // Calculate totals based on cart or single item
  const calculateTotals = () => {
    if (isCartCheckout) {
      const subtotal = cartItemsFromState?.reduce((sum, item) => {
        const price = parseFloat(item?.menu_item?.price_per_plate || item?.menu_item?.price || 0);
        return sum + (price * item?.quantity);
      }, 0);
      const deliveryFee = deliveryOption === 'delivery' ? 3.99 : 0;
      const serviceFee = SERVICE_FEE;
      const tax = (subtotal + deliveryFee + serviceFee) * TAX_RATE;
      const total = subtotal + deliveryFee + serviceFee + tax;
      return { subtotal, deliveryFee, serviceFee, tax, total };
    } else {
      const subtotal = (currentDish?.price_per_plate || currentDish?.price || 0) * quantity;
      const deliveryFee = deliveryOption === 'delivery' ? 3.99 : 0;
      const serviceFee = SERVICE_FEE;
      const tax = (subtotal + deliveryFee + serviceFee) * TAX_RATE;
      const total = subtotal + deliveryFee + serviceFee + tax;
      return { subtotal, deliveryFee, serviceFee, tax, total };
    }
  };

  const { subtotal, deliveryFee, serviceFee, tax, total } = calculateTotals();

  // Show authentication prompt if user is not logged in
  if (!user) {
    return (
      <>
        <Helmet>
          <title>Order Placement - HungryElph</title>
          <meta name="description" content="Complete your order with customization options and secure checkout" />
        </Helmet>

        <RoleBasedNavigation userRole={null} isAuthenticated={false} notificationCount={0} />

        <div className="min-h-screen bg-background py-6 md:py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4 md:mb-6">
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm md:text-base">{isCartCheckout ? 'Back to Cart' : 'Back to Kitchen'}</span>
            </button>

            <AuthenticationPrompt
              title="Sign in to place your order"
              message="Please sign in to complete your order and enjoy delicious home-cooked meals."
            />
          </div>
        </div>
      </>
    );
  }

  if (orderPlaced) {
    return (
      <>
        <Helmet>
          <title>Order Confirmed - HungryElph</title>
        </Helmet>

        <RoleBasedNavigation />

        <div className="min-h-screen bg-background py-12">
          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
            <div className="bg-card rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={48} color="var(--color-success)" />
              </div>

              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Order Confirmed!
              </h1>

              <p className="text-muted-foreground mb-8">
                {isCartCheckout 
                  ? 'Your orders have been placed successfully. You can track them in your dashboard.' :'Your order has been placed successfully. You can track it in your dashboard.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/customer-dashboard')} size="lg">
                  <Icon name="Home" size={20} />
                  Go to Dashboard
                </Button>
                <Button onClick={() => navigate('/customer-dashboard')} variant="outline" size="lg">
                  <Icon name="ShoppingBag" size={20} />
                  Browse More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Complete Your Order - HungryElph</title>
        <meta name="description" content="Complete your order with customization options and secure checkout" />
      </Helmet>

      <RoleBasedNavigation />

      <div className="min-h-screen bg-background py-6 md:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4 md:mb-6">
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm md:text-base">{isCartCheckout ? 'Back to Cart' : 'Back to Kitchen'}</span>
          </button>

          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            {/* Left Column - Order Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Page Header */}
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                  Complete Your Order
                </h1>
                <p className="text-muted-foreground">
                  {isCartCheckout 
                    ? `Review your ${cartItemsFromState?.length} item${cartItemsFromState?.length !== 1 ? 's' : ''} and schedule delivery`
                    : 'Review your order details and schedule delivery'}
                </p>
              </div>

              {/* Cart Items or Single Dish */}
              {isCartCheckout ? (
                <div className="bg-card rounded-xl p-6">
                  <h2 className="text-xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="ShoppingCart" size={24} />
                    Your Cart Items
                  </h2>
                  <div className="space-y-4">
                    {cartItemsFromState?.map((item) => (
                      <div key={item?.id} className="flex gap-4 pb-4 border-b border-border last:border-0 last:pb-0">
                        <img
                          src={item?.menu_item?.image || 'https://via.placeholder.com/80'}
                          alt={item?.menu_item?.image_alt || item?.menu_item?.name}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground">{item?.menu_item?.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {item?.menu_item?.supplier?.business_name}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm text-muted-foreground">Qty: {item?.quantity}</span>
                            <span className="font-semibold text-foreground">
                              ${((item?.menu_item?.price_per_plate || item?.menu_item?.price || 0) * item?.quantity)?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <DishDetailsCard
                  dish={currentDish}
                  quantity={quantity}
                  onQuantityChange={setQuantity}
                />
              )}

              {/* Customization Section - Only for single item orders */}
              {!isCartCheckout && (
                <CustomizationSection
                  customizations={customizations}
                  onCustomizationChange={handleCustomizationChange}
                  specialInstructions={specialInstructions}
                  onInstructionsChange={setSpecialInstructions}
                />
              )}

              {/* Delivery Scheduling */}
              <DeliveryScheduling
                deliveryOption={deliveryOption}
                onDeliveryOptionChange={setDeliveryOption}
                addresses={addresses}
                selectedAddress={selectedAddress}
                onAddressChange={handleAddressChange}
                scheduledDate={scheduledDate}
                scheduledTime={scheduledTime}
                onScheduleChange={handleScheduleChange}
              />

              {/* Special Instructions */}
              <div className="bg-card rounded-xl p-6">
                <h2 className="text-xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="MessageSquare" size={24} />
                  Special Instructions
                </h2>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e?.target?.value)}
                  placeholder="Any special requests or dietary requirements?"
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={4}
                />
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <OrderSummary
                  dish={currentDish}
                  quantity={quantity}
                  deliveryOption={deliveryOption}
                  subtotal={subtotal}
                  deliveryFee={deliveryFee}
                  serviceFee={serviceFee}
                  tax={tax}
                  total={total}
                  onPlaceOrder={handlePlaceOrder}
                  isProcessing={isProcessing}
                  disabled={!selectedAddress || !scheduledDate || !scheduledTime}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderPlacement;